import { quoteTimestamp } from './quote-status';
/**
 * 画线工作台 · 数据层。后端 /api/v2/drawing-desk/*。
 *
 * 当日未收盘 bar **不走这里** —— 它由既有 /api/v1/unified/quote 单独取，
 * 因为画布 GET 仅访问本地数据（D9）；已收盘日线由独立的 history-sync POST 按需补齐。
 */
import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import type { CanvasResponse, DrawObject, PaneSpec, Period, PriceBasis, PriceScale, XPeriodMode } from './types';
import { historySymbolKey } from './history-sync';
import type { LiveQuote } from './forming';
import { SWING_PERIODS } from './swing-path';
import { recognitionBody, type AutoResult, type WaveSettings } from './auto-trendlines';
import type { DisplayBar } from './types';
import { ObjectWriter, type CreateResult, type WriterHooks } from './writes';

const V2 = '/../v2/drawing-desk';

/** 画布查询的键与取数。`useCanvas` 与「最近查看」预取共用 —— 两份定义会让预取的缓存命中不了。 */
export const canvasHistoryBars = (period: Period, symbol = '') => /^(sh(?:000|51|52|56|58)|sz(?:399|15|16|18))\d+$/.test(historySymbolKey(symbol)) ? 1000 : period === 'daily' ? 1000 : period === 'weekly' ? 400 : period === 'monthly' ? 240 : 600;

export function canvasQuery(symbol: string, period: Period, bars = canvasHistoryBars(period,symbol), fullHistory = false) {
  return {
    queryKey: ['drawing-desk', 'canvas', symbol, period, bars, fullHistory ? 'all_available' : 'window'] as const,
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      const { data } = await api.get<CanvasResponse>(
        `${V2}/canvas/${symbol}`, { params: { period, bars, ...(fullHistory ? { full_history: true } : {}) }, signal },
      );
      return data;
    },
    staleTime: 30_000,
  };
}

/**
 * 空闲时预取「最近查看」里除当前外的前几只（提速计划 阶段 5）。**一只一只来**，不和正在进行的
 * 操作抢；已在缓存且未过期的不重取。画布端点只读本地库、零外部请求（D9），不触碰限速纪律。
 */
export function usePrefetchRecent(symbols: string[], current: string, period: Period, ready: boolean) {
  const qc = useQueryClient();
  const key = symbols.join(',');
  useEffect(() => {
    if (!ready) return;
    const targets = symbols.filter((s) => s && s !== current).slice(0, 3);
    if (targets.length === 0) return;
    let cancelled = false;
    const run = async () => {
      for (const s of targets) {
        if (cancelled) return;
        await qc.prefetchQuery(canvasQuery(s, period)).catch(() => {});
      }
    };
    const w = window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number;
                                   cancelIdleCallback?: (h: number) => void };
    const handle = w.requestIdleCallback ? w.requestIdleCallback(() => void run(), { timeout: 3000 })
                                          : window.setTimeout(() => void run(), 1500);
    return () => {
      cancelled = true;
      if (w.cancelIdleCallback) w.cancelIdleCallback(handle); else window.clearTimeout(handle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, current, period, ready]);
}

export function useCanvas(symbol: string, period: Period, bars = canvasHistoryBars(period,symbol), fullHistory = false, enabled = true, placeholderCanvas?: CanvasResponse) {
  return useQuery({
    ...canvasQuery(symbol, period, bars, fullHistory),
    enabled: enabled && Boolean(symbol),
    // **切标的时保留上一只的画布，直到新的到位。** 不保留的话新 key 一开始 data 为空，
    // 页面主体（标的库 / K 线 / 仪表）整块卸载成一个「加载中」再重新挂载 ——
    // 用户看到的就是整页刷新（2026-09-14 反馈）。占位期间页面必须按 `isPlaceholderData`
    // 禁止落笔与改画布：那份数据是上一只票的。
    placeholderData: (previous: CanvasResponse | undefined) => previous ?? placeholderCanvas,
    // Every asset uses the mounted history-sync POST for maintenance progress.
    // GET prefetch is read-only and never owns a second polling/producer path.
  });
}

/** The swing layer gets full local history; trendline recognition keeps its original query. */
export function useSwingCanvas(symbol: string, period: Period, swingEnabled: boolean, requestedBars?: number) {
  const fullHistory = swingEnabled && (SWING_PERIODS as readonly string[]).includes(period);
  const ordinaryCanvas = useCanvas(symbol, period, requestedBars);
  const fullCanvas = useCanvas(symbol, period, undefined, true, fullHistory, ordinaryCanvas.data);
  return { ordinaryCanvas, canvas: fullHistory ? fullCanvas : ordinaryCanvas };
}

/**
 * 今日实时报价 → 「今日·未收盘」bar 的原料（规格 D1，见 forming.ts）。
 *
 * 走既有 `/api/v1/unified/quote`（K 线/实时报价 SOT），不在画线工作台后端再开一个数据入口 ——
 * 画布端点零外部 HTTP（D9）。60 秒刷新一次；页面切到后台时 react-query 默认不轮询。
 * 分钟线不需要它（intraday_kline 盘中已有今天），`enabled` 关掉。
 */
export function useLiveQuote(symbol: string, enabled: boolean) {
  return useQuery({
    queryKey: ['drawing-desk', 'live-quote', symbol] as const,
    queryFn: async (): Promise<LiveQuote & { symbol: string } | null> => {
      const { data } = await api.get<{
        success: boolean;
        data: { open: number | null; high: number | null; low: number | null; close: number | null;
                volume: number | null; amount: number | null; change_percent: number | null;
                timestamp: string | null; volume_unit?: LiveQuote['volumeUnit'] };
        meta?: { actual_data_date?: string | null; source?: string; timestamp_kind?: string };
      }>(`/unified/quote/${symbol}`);
      const q = data?.data;
      const stamped = quoteTimestamp(q?.timestamp);
      const date = data?.meta?.actual_data_date || stamped.day;
      if (!data?.success || !q || !date || q.close == null || q.open == null
          || q.high == null || q.low == null) return null;
      return {
        symbol, date, open: q.open, high: q.high, low: q.low, close: q.close,
        // 成交额 0 = 该次写入没带（生产实测同一只票同一分钟内在 15.04 亿与 0.0 之间翻转），
        // 按「不知道」处理成 null，不能显示成「成交额 0」
        volume: q.volume ?? 0, amount: q.amount && q.amount > 0 ? q.amount : null, changePct: q.change_percent,
        volumeUnit: q.volume_unit ?? 'shares', volumeUnknown: q.volume == null,
        asOf: data?.meta?.timestamp_kind === 'date_only' || (data?.meta?.source === 'database' && q.timestamp?.slice(11,19) === '00:00:00') ? null : stamped.time,
        source: data?.meta?.source, timestampKind: data?.meta?.timestamp_kind,
      };
    },
    enabled: Boolean(symbol) && enabled,
    staleTime: 30_000,
    refetchInterval: 60_000,
    // 报价失败只意味着「今天这根暂时不画」，不能让画布进入错误态
    retry: 1,
  });
}

/**
 * 图形增删改 —— 先改画面、后台存（提速计划 阶段 1，逻辑与测试在 writes.ts）。
 *
 * 返回一个**身份稳定**的写入器：它持有每条线的队列与临时 id，换一次实例就丢一次在途状态。
 * 回调走 ref，页面每次渲染可换新闭包而不必重建写入器。
 */
export function useObjectWriter(hooks: WriterHooks): ObjectWriter {
  const qc = useQueryClient();
  const hooksRef = useRef(hooks);
  hooksRef.current = hooks;
  const ref = useRef<ObjectWriter>();
  if (!ref.current) {
    ref.current = new ObjectWriter(qc, {
      create: async (body) => (await api.post<CreateResult>(`${V2}/objects`, body)).data,
      patch: async (id, patch) => (await api.patch<DrawObject>(`${V2}/objects/${id}`, patch)).data,
      remove: async (id) => { await api.delete(`${V2}/objects/${id}`); },
    }, {
      onError: (m) => hooksRef.current.onError(m),
      onNotice: (m) => hooksRef.current.onNotice(m),
      onIdResolved: (a, b) => hooksRef.current.onIdResolved(a, b),
    });
  }
  const writer = ref.current;
  // 页面隐藏/关闭时把合并窗口里还没发出的样式立即发出，不丢最后一次改色
  useEffect(() => {
    const h = () => writer.flush();
    window.addEventListener('pagehide', h);
    document.addEventListener('visibilitychange', h);
    return () => {
      window.removeEventListener('pagehide', h);
      document.removeEventListener('visibilitychange', h);
      writer.flush();
    };
  }, [writer]);
  return writer;
}

/**
 * 指标参数（窗格布局）**先改画面、后台存**（提速计划 阶段 2，用户裁决：即时保存、无撤销）。
 *
 * 之前保存后整张画布缓存失效、重拉 K 线与图形（0.3–1.2s），而指标本来就在浏览器里算。
 * 现在：本地立即替换布局 → 300ms 内的连续改动合并成一次请求 → 响应里的规范化布局（补全参数、
 * 指纹、窗格名）合并回缓存。只有**指纹变了**（RSI 周期、MACD、CCI、BOLL 这类改变曲线形状的
 * 参数）才重取画布，因为图形的健康度要按新指纹重判；均线周期、显隐、线宽不重取。
 */
export function usePaneLayoutWriter(symbol: string) {
  const qc = useQueryClient();
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latest = useRef<{ id: number; layout: PaneSpec[] } | null>(null);

  const apply = (id: number, fn: (c: CanvasResponse['canvas']) => CanvasResponse['canvas']) => {
    qc.setQueriesData<CanvasResponse>(
      { queryKey: ['drawing-desk', 'canvas', symbol] },
      (old) => (old && old.canvas.id === id ? { ...old, canvas: fn(old.canvas) } : old),
    );
  };

  const flush = async () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    const job = latest.current;
    latest.current = null;
    if (!job) return;
    const before = new Map(job.layout.map((p) => [p.pane_key, p.series_fp]));
    try {
      const { data } = await api.patch<{ pane_layout: PaneSpec[] }>(`${V2}/canvas/${job.id}`, {
        pane_layout: job.layout.map(({ pane_key, indicator_id, params, height_ratio, hidden }) => ({
          pane_key, indicator_id, params, height_ratio, ...(hidden===undefined?{}:{hidden}),
        })),
      });
      // 期间用户又改了 → 以本地为准，不用这份响应覆盖
      if (latest.current) return;
      apply(job.id, (c) => ({ ...c, pane_layout: data.pane_layout }));
      const fpChanged = data.pane_layout.some((p) => before.has(p.pane_key) && before.get(p.pane_key) !== p.series_fp);
      if (fpChanged) void qc.invalidateQueries({ queryKey: ['drawing-desk', 'canvas', symbol] });
    } catch {
      void qc.invalidateQueries({ queryKey: ['drawing-desk', 'canvas', symbol] });
      throw new Error('指标参数没保存上，已恢复成服务器上的设置');
    }
  };

  // 页面隐藏/关闭时把合并窗口里的改动立即发出
  useEffect(() => {
    const h = () => { if (latest.current) void flush().catch(() => {}); };
    window.addEventListener('pagehide', h);
    return () => { window.removeEventListener('pagehide', h); h(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol]);

  return {
    /** 立即生效；返回的 Promise 在真正保存（或失败）后结束。 */
    change: (id: number, layout: PaneSpec[], immediate = false) => {
      apply(id, (c) => ({ ...c, pane_layout: layout }));
      latest.current = { id, layout };
      // Mode switches need their own completion; a later parameter edit must not cancel it.
      if (immediate) return flush();
      if (timer.current) clearTimeout(timer.current);
      return new Promise<void>((resolve, reject) => {
        timer.current = setTimeout(() => { flush().then(resolve, reject); }, 300);
      });
    },
    flush,
  };
}

export function usePatchCanvas(symbol: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: {
      id: number;
      patch: Partial<{ price_basis: PriceBasis; price_scale: PriceScale;
                       xperiod_mode: XPeriodMode; pane_layout: unknown[] }>;
    }) => {
      const { data } = await api.patch(`${V2}/canvas/${id}`, patch);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['drawing-desk', 'canvas', symbol] }),
  });
}


export interface SymbolItem { symbol: string; name: string; delisted?: boolean; kind?: 'index' | 'etf' | 'industry' | 'concept' | 'hk_stock' | 'hk_index' | 'us_stock' | 'us_index' | 'us_etf' | 'kr_stock'; catalog_as_of?: string }

/** 标的搜索。选项全集来自专用 catalog 接口，不从当前响应反推（前端 SSOT 纪律）。 */
export function useSymbolSearch(q: string, enabled: boolean) {
  return useQuery({
    queryKey: ['drawing-desk', 'symbols', q] as const,
    queryFn: async () => {
      const { data } = await api.get<{ items: SymbolItem[] }>(`${V2}/symbols`, { params: { q, limit: 100 } });
      return data;
    },
    enabled,
    staleTime: 5 * 60_000,
  });
}

export interface RecentSymbol { symbol: string; name: string | null }

export interface DeskPrefs {
  zoom_speed: number;
  /** 最近查看（新 → 旧，至多 12 只）。首项 = 上次看的那只，进页面默认打开它。 */
  recent_symbols: RecentSymbol[];
}

/** 记一次「看了这只票」。搜索框与标的库抽屉切票走同一条路。返回最新偏好，直接写回缓存。 */
export function useRecordRecent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (symbol: string) =>
      (await api.post<DeskPrefs>(`${V2}/prefs/recent`, { symbol })).data,
    onSuccess: (d) => qc.setQueryData(['drawing-desk', 'prefs'], d),
  });
}

export function useClearRecent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.delete<DeskPrefs>(`${V2}/prefs/recent`)).data,
    onSuccess: (d) => qc.setQueryData(['drawing-desk', 'prefs'], d),
  });
}

export function usePrefs() {
  return useQuery({
    queryKey: ['drawing-desk', 'prefs'] as const,
    queryFn: async () => (await api.get<DeskPrefs>(`${V2}/prefs`)).data,
    staleTime: Infinity,
  });
}

export function usePatchPrefs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<DeskPrefs>) =>
      (await api.patch<DeskPrefs>(`${V2}/prefs`, patch)).data,
    onSuccess: (d) => qc.setQueryData(['drawing-desk', 'prefs'], d),
  });
}


export function useAutoTrendlines(symbol: string, period: Period, bars: DisplayBar[], scale: PriceScale,
  basis: PriceBasis, unclassifiedGaps: boolean, enabled: boolean, waveSettings?:WaveSettings, missingSessions:string[] = []) {
  return useQuery({
    queryKey: ['drawing-desk', 'auto-trendlines', symbol, period, bars, scale, basis, unclassifiedGaps, waveSettings, missingSessions],
    queryFn: async ({ signal }) => (await api.post<AutoResult>(`${V2}/trendlines/recognize`,
      {...recognitionBody(symbol, period, bars, scale, basis, unclassifiedGaps, missingSessions),wave_config:waveSettings}, { signal })).data,
    enabled: enabled && Boolean(symbol) && bars.length > 0,
    staleTime: 30_000, retry: 1,
  });
}
